import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getCalendarFeedToken } from '@/lib/calendarFeedTokens';
import { AlertCircle, CheckCircle2, Cloud, Copy, Loader2, RefreshCcw } from 'lucide-react';

const DEFAULT_CALENDAR_ID = 'primary';

export function ICSStatusCard() {
  const { toast } = useToast();
  const auth = useMemo(() => getAuth(), []);
  const [userId, setUserId] = useState<string | null>(null);
  const [calendarId] = useState<string>(DEFAULT_CALENDAR_ID);
  const [feedUrl, setFeedUrl] = useState<string>('');
  const [validateUrl, setValidateUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [lastChecked, setLastChecked] = useState<string | null>(null);
  const [status, setStatus] = useState<'idle' | 'ok' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState<string>('Not validated yet.');

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setUserId(user?.uid ?? null);
    });
    return unsub;
  }, [auth]);

  useEffect(() => {
    if (!userId) {
      setFeedUrl('');
      setValidateUrl('');
      return;
    }

    let cancelled = false;
    const load = async () => {
      try {
        const existing = await getCalendarFeedToken(userId, calendarId);
        if (cancelled) return;
        if (existing?.feedUrl) {
          setFeedUrl(existing.feedUrl);
          setValidateUrl(existing.feedUrl);
          setStatus('idle');
          setStatusMessage('Loaded active ICS feed.');
        }
      } catch {
        if (!cancelled) {
          setStatus('error');
          setStatusMessage('Could not load current feed token.');
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [calendarId, userId]);

  const handleCopy = useCallback(() => {
    if (!feedUrl) return;
    navigator.clipboard?.writeText(feedUrl);
    toast({ title: 'Copied', description: 'ICS feed URL copied to clipboard.' });
  }, [feedUrl, toast]);

  const handleValidate = useCallback(async () => {
    if (!validateUrl) return;
    setIsLoading(true);
    setStatus('idle');
    setStatusMessage('Checking feed…');
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);

    try {
      const res = await fetch(validateUrl, {
        method: 'GET',
        mode: 'cors',
        signal: controller.signal,
      });
      const ok = res.ok;
      const ct = res.headers.get('content-type') || '';
      setStatus(ok ? 'ok' : 'error');
      setStatusMessage(
        ok
          ? ct.includes('text/calendar')
            ? 'Feed is reachable and returns text/calendar.'
            : 'Feed reachable; content-type not text/calendar.'
          : `Request failed (${res.status}).`
      );
      setLastChecked(new Date().toISOString());
    } catch (error) {
      const aborted = (error as { name?: string })?.name === 'AbortError';
      setStatus('error');
      setStatusMessage(aborted ? 'Request timed out. Check hosting rewrite.' : 'Could not reach feed (CORS or network).');
      setLastChecked(new Date().toISOString());
    } finally {
      clearTimeout(timeout);
      setIsLoading(false);
    }
  }, [validateUrl]);

  const statusBadge = () => {
    if (status === 'ok') return <Badge variant="default" className="bg-emerald-100 text-emerald-800 border border-emerald-200">Healthy</Badge>;
    if (status === 'error') return <Badge variant="destructive" className="text-xs">Needs attention</Badge>;
    return <Badge variant="outline" className="text-xs">Not checked</Badge>;
  };

  return (
    <Card className="bg-card/50 backdrop-blur border-border/50">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Cloud className="w-4 h-4" /> ICS status
        </CardTitle>
        <CardDescription>
          Validate the read-only ICS feed and surface the last check, per the sync spec (Phase 1).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
          <span>Default calendar: <span className="font-semibold text-foreground">{calendarId}</span></span>
          <div className="flex items-center gap-2">
            {statusBadge()}
            {lastChecked ? <span>Last checked {new Date(lastChecked).toLocaleString()}</span> : <span>Not checked yet</span>}
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="feed-url">ICS feed URL (user-scoped)</Label>
          <div className="flex gap-2">
            <Input id="feed-url" value={feedUrl} readOnly placeholder="Generate from Calendar Sync card" className="flex-1" />
            <Button variant="outline" onClick={handleCopy} disabled={!feedUrl}>
              <Copy className="w-4 h-4 mr-2" />
              Copy
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground">Tokenized, read-only, scoped to the signed-in user + calendar. Manage rotation/revoke in the Calendar Sync card.</p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="validate-url">Validate URL</Label>
          <div className="flex gap-2">
            <Input
              id="validate-url"
              value={validateUrl}
              onChange={(e) => setValidateUrl(e.target.value)}
              placeholder="https://.../calendar-feed/..."
              className="flex-1"
              autoComplete="off"
            />
            <Button onClick={handleValidate} disabled={!validateUrl || isLoading}>
              {isLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCcw className="w-4 h-4 mr-2" />}
              Validate
            </Button>
          </div>
          <div className="flex items-start gap-2 text-xs text-muted-foreground">
            {status === 'ok' ? <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5" /> : <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5" />} 
            <span>{statusMessage}</span>
          </div>
        </div>

        <div className="rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-xs space-y-1">
          <div className="font-semibold text-foreground/80">Spec alignment</div>
          <ul className="list-disc list-inside space-y-0.5 text-muted-foreground">
            <li>Read-only ICS feed per calendar with tokenized access.</li>
            <li>Ensure hosting rewrite `/calendar-feed/**` is deployed.</li>
            <li>Plan audit log (`syncLogs`, 30d) for feed hits/errors.</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}

export default ICSStatusCard;
